import { FC, useCallback, useState } from "react";
import { Flex, Stack, StackDivider } from "@chakra-ui/react";
import { theme } from "@ovou-inc/ovou-ui";
import { useBoolean } from "@chakra-ui/react";
import { useCustomerPortal } from "api/customerPortal";
import { CHECKOUT_URL, PAGES } from "constants/pages";
import { CHECKOUT_TYPE } from "constants/Texts";
import { useUpgradeModal } from "hooks/useUpgradeModal";
import { useTracking } from "hooks";
import { FEATURES } from "constants/features";
import ManageSubscription from "features/manageSubscription";

import SidebarHeader from "./Header";
import MenuItemComponent from "./MenuItem";
import { useGetMenuItems } from "./hook/useGetMenuItems";
import { ISidebar } from "../../types/ISidebar";

export const Sidebar: FC<
  Omit<ISidebar, "mainItems" | "secondaryItems">
> = props => {
  const track = useTracking();
  const { openModal } = useUpgradeModal();
  const { mutate: fireCustomerPortal } = useCustomerPortal();

  const [
    isManageSubscriptionOpen,
    { on: openManageSubscription, off: closeManageSubscription },
  ] = useBoolean();

  const [customerPortal, setCustomerPortal] = useState("");

  const handleStripePortalClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      try {
        const data = await fireCustomerPortal({
          data: {
            cancel_url: CHECKOUT_URL.CANCEL,
            success_url: CHECKOUT_URL.SUCCESS,
            type: CHECKOUT_TYPE,
          },
        });
        if (data?.url) {
          setCustomerPortal(data?.url);
          openManageSubscription();
        }
      } catch (error) {
        console.error("Failed to get Stripe portal URL:", error);
      }
    },
    [fireCustomerPortal, openManageSubscription],
  );

  const handleUpgradeClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      track({
        upgrade: true,
        customTrackingProps: { source: FEATURES.UPGRADE_NOW },
      });
      openModal();
    },
    [track, openModal],
  );

  const handleShopClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = `${PAGES.SHOP}?source=admin`;
  }, []);

  const { mainItems, secondaryItems } = useGetMenuItems(
    handleStripePortalClick,
    handleUpgradeClick,
    handleShopClick,
  );

  return (
    <>
      <SidebarView
        {...props}
        mainItems={mainItems}
        secondaryItems={secondaryItems}
      />

      <ManageSubscription
        isOpen={isManageSubscriptionOpen}
        onClose={closeManageSubscription}
        customerPortal={customerPortal}
      />
    </>
  );
};

export const SidebarView: FC<ISidebar> = ({
  mainItems,
  secondaryItems,
  isMobile,
  isOpen,
  close,
}) => {
  return (
    <div
      className={`flex flex-shrink-0 border-r border-gray-300 transition-all duration-150 ease-in-out ${
        isMobile ? "fixed inset-0 z-40" : ""
      }`}
      style={{ transform: isOpen ? "translateX(0)" : "translateX(-100%)" }}
    >
      <Flex
        flexDirection="column"
        backgroundColor={theme.palette.brand.primary.white}
        width={isMobile ? "full" : 72}
      >
        <SidebarHeader close={close} isMobile={isMobile} />

        <Flex
          flex={1}
          flexDirection="column"
          justifyContent="space-between"
          overflowY="auto"
          marginTop={8}
          marginBottom={4}
          paddingX={4}
        >
          <Stack spacing={0}>
            {mainItems.map(item => (
              <MenuItemComponent key={item.title} {...item} close={close} />
            ))}
          </Stack>

          <Stack
            marginTop={4}
            divider={
              <StackDivider borderColor={theme.palette.ui.greys.grey4} />
            }
          >
            {secondaryItems.map(item => (
              <MenuItemComponent
                key={item.title}
                {...item}
                textColor={theme.palette.brand.primary.black}
                sx={{
                  svg: {
                    color: theme.palette.ui.greys.grey1,
                  },
                }}
              />
            ))}
          </Stack>
        </Flex>
      </Flex>
    </div>
  );
};
